import React, { useEffect, useState } from 'react';

import { useInView } from '../../hooks/useInView';
import type { Brand } from './brands';

import './FeaturedBrands.css';

// Matches the CSS reveal animation total duration (last stagger delay + duration + buffer).
// Bumping any keyframe delay in FeaturedBrands.css means revisiting this number.
const REVEAL_COMPLETE_MS = 720;
// Trigger as soon as any of the section enters the viewport; a positive
// bottom rootMargin pre-fires the observer just before the section is
// reached so the cells start animating without the user having to scroll
// past the top edge.
const REVEAL_OPTIONS: IntersectionObserverInit = { threshold: 0, rootMargin: '0px 0px 120px 0px' };

type FeaturedBrandsProps = {
    brandGrid: Brand[][];
    heading: string;
    body: string;
};

export function FeaturedBrands({ brandGrid, heading, body }: FeaturedBrandsProps) {
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
    const [revealed, setRevealed] = useState(false);
    const [sectionRef, inView] = useInView<HTMLElement>(REVEAL_OPTIONS);
    const hasSelectedBrand = selectedBrand !== null;

    useEffect(() => {
        if (!inView) { return; }
        const timer = setTimeout(() => setRevealed(true), REVEAL_COMPLETE_MS);
        return () => clearTimeout(timer);
    }, [inView]);

    useEffect(() => {
        if (!hasSelectedBrand) { return; }
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setSelectedBrand(null);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [hasSelectedBrand]);

    const resetSelection = () => {
        if (hasSelectedBrand) {
            setSelectedBrand(null);
        }
    };

    const handleBrandClick = (event: React.MouseEvent<HTMLButtonElement>, brand: Brand) => {
        event.stopPropagation();

        if (!hasSelectedBrand) {
            setSelectedBrand(brand);
            return;
        }

        setSelectedBrand(null);
    };

    return (
        <section
            ref={sectionRef}
            className={`featured-brands${hasSelectedBrand ? ' featured-brands--detail' : ''}${inView ? ' featured-brands--in-view' : ''}${revealed ? ' featured-brands--revealed' : ''}`}
            onClick={resetSelection}
        >
            <div className="featured-brands__text">
                <h2 className='featured-brands__heading'>
                    {selectedBrand ? selectedBrand.name : heading}
                </h2>
                {selectedBrand ? (
                    <div className="featured-brands__trust">
                        <div className="featured-brands__avatars" aria-hidden="true">
                            {selectedBrand.athletePreviews.map((src, idx) => (
                                <span key={`${src}-${idx}`} className="featured-brands__avatar">
                                    <img src={src} alt="" loading="lazy" decoding="async" />
                                </span>
                            ))}
                            <span className={`featured-brands__avatar-more faves-glass${selectedBrand.trustedByCount >= 100 ? ' featured-brands__avatar-more--compact' : ''}`}>+{selectedBrand.trustedByCount}</span>
                        </div>
                        <p className="featured-brands__trust-copy">
                            Trusted by {selectedBrand.trustedByCount}+ top athletes on faves
                        </p>
                    </div>
                ) : (
                    <p className="featured-brands__body faves-body">{body}</p>
                )}
            </div>

            <div className="featured-brands__grid">
                {brandGrid.map((row, rowIdx) => (
                    <div key={rowIdx} className="featured-brands__row">
                        {row.map((brand, cellIdx) => {
                            const isSelected = selectedBrand === brand;

                            return (
                                <button
                                    key={`${rowIdx}-${cellIdx}`}
                                    type="button"
                                    className={`featured-brands__cell faves-glass${isSelected ? ' featured-brands__cell--selected' : ''}${hasSelectedBrand && !isSelected ? ' featured-brands__cell--dimmed' : ''}`}
                                    onClick={(event) => handleBrandClick(event, brand)}
                                    aria-pressed={isSelected}
                                    aria-label={isSelected ? `${brand.name} selected` : `Show ${brand.name} detail`}
                                >
                                    <img src={brand.logo} alt={brand.name} className="featured-brands__logo" loading="lazy" decoding="async" />
                                </button>
                            );
                        })}
                    </div>
                ))}
            </div>
        </section>
    );
}
